import {useCallback, useEffect, useRef, useState} from 'react'

import {tablePagination} from '../styles.js'

// The page size is shared by all tables
const pageSizeKey = '_pageSize'

function validPageSize(value: unknown): number | undefined {
    const size = typeof value === 'number' ? value : parseInt(String(value), 10)

    return Number.isFinite(size) && size > 0 ? size : undefined
}

function readPageSize(): number {
    try {
        return validPageSize(localStorage.getItem(pageSizeKey)) ?? tablePagination.defaultPageSize
    } catch {
        return tablePagination.defaultPageSize
    }
}

interface PaginationOptions {
    /** Number of rows over all pages, undefined when unknown (server side without totalRowsCount) */
    totalRows: number | undefined
    /** No clamping while loading: the rows are about to change */
    loading: boolean
    onPagination?: (pageSize: number, page: number) => void
}

/**
 * The current page and page size. The page stays within the pages there are when the rows or the page size change.
 * Choosing a page or page size calls onPagination (for server side pagination), selectPage (the ref) doesn't: the
 * caller fetched that page already.
 */
export default function useTablePagination({totalRows, loading, onPagination}: PaginationOptions) {
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(readPageSize)

    const nrOfPages = Math.max(1, Math.ceil((totalRows ?? 0) / pageSize))
    const clamp = !loading && totalRows !== undefined
    const currentPage = clamp ? Math.min(page, nrOfPages) : page

    useEffect(() => {
        if (currentPage !== page) {
            setPage(currentPage)
        }
    }, [currentPage, page])

    const latest = useRef({currentPage, pageSize, nrOfPages, onPagination})
    latest.current = {currentPage, pageSize, nrOfPages, onPagination}

    const goToPage = useCallback((page: number) => {
        const {currentPage, pageSize, nrOfPages, onPagination} = latest.current
        const next = Math.max(1, Math.min(page, nrOfPages))

        if (next !== currentPage) {
            setPage(next)
            onPagination?.(pageSize, next)
        }
    }, [])

    // Keeps the first row of the current page in view
    const changePageSize = useCallback((value: unknown) => {
        const size = validPageSize(value)
        const {currentPage, pageSize, onPagination} = latest.current

        if (!size || size === pageSize) {
            return
        }

        try {
            localStorage.setItem(pageSizeKey, String(size))
        } catch {
            // Only for this page then
        }

        const next = Math.floor((currentPage - 1) * pageSize / size) + 1

        setPageSize(size)
        setPage(next)
        onPagination?.(size, next)
    }, [])

    const selectPage = useCallback((page: number) => {
        setPage(Math.max(1, Math.floor(page) || 1))
    }, [])

    return {page: currentPage, pageSize, nrOfPages, goToPage, changePageSize, selectPage}
}
